// First-run tours (D-142): a few steps for each role, shown once to each member the first time they open
// the CRM, never again once finished or skipped (remembered on the server, so on every device). A card in
// the corner, not a dialog: it never blocks the page it describes.
import { useQueryClient } from '@tanstack/react-query';
import { Button, cn } from '@tatra/ui';
import { Compass, X } from 'lucide-react';
import { useState } from 'react';
import { post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

interface Step {
  title: string;
  body: string;
}

const SELLING: Step[] = [
  {
    title: 'Today is your list',
    body: 'Your calls, follow-ups and visits for the day are on Today, most urgent first. Start there every morning.',
  },
  {
    title: 'Every lead in one place',
    body: 'Leads shows everyone who enquired. Open one to call, note, book a visit or move it on — all from its own page.',
  },
  {
    title: 'Add in seconds',
    body: 'The + button adds a lead, a walk-in, a visit or a task from any screen.',
  },
];

/** Steps per role; roles without their own use the closest one. */
const TOURS: Record<string, Step[]> = {
  sales_executive: SELLING,
  pre_sales: [
    {
      title: 'Your calling queue',
      body: 'New and unqualified leads are on Today and in Leads. Call the newest first: the response clock is running.',
    },
    ...SELLING.slice(1),
  ],
  team_lead: [
    {
      title: 'Your team at a glance',
      body: 'Your dashboard shows overdue follow-ups, unattended leads and calls for the people you lead.',
    },
    {
      title: 'Step in where it matters',
      body: 'Leads → Advanced filters → Waiting for attention lists the leads nobody has touched yet.',
    },
    SELLING[2]!,
  ],
  sales_head: [
    {
      title: 'Where the pipeline leaks',
      body: 'Your dashboard shows stage conversion, ageing and the forecast. Tap any number to see the leads behind it.',
    },
    {
      title: 'Approvals',
      body: 'Discounts and cancellations waiting for you are counted on the dashboard and listed under Approvals.',
    },
    {
      title: 'Insights',
      body: 'Insights compares every trend with the period before, and lets you build your own reports.',
    },
  ],
  director: [
    {
      title: 'Your numbers, with their story',
      body: 'The dashboard compares each number with the previous period and last year. Tap a number to see what is behind it.',
    },
    {
      title: 'Projects and inventory',
      body: 'Projects shows stock, prices and the site plan for every launch.',
    },
    {
      title: 'Settings',
      body: 'Under Settings you change fields, lists, alerts and templates yourself — no code needed.',
    },
  ],
  project_head: [
    {
      title: 'Your projects',
      body: 'Your dashboard covers only your projects: visits, bookings, inventory and pricing.',
    },
    {
      title: 'Inventory',
      body: 'Projects → Inventory shows every unit and its status; holds and blocks are one tap.',
    },
    { title: 'Site plans', body: 'The site plan shows availability live and can be shared on your website.' },
  ],
  marketing: [
    {
      title: 'Where leads come from',
      body: 'Marketing → Attribution shows each channel and campaign from spend to bookings.',
    },
    {
      title: 'Campaigns',
      body: 'Send e-mail and SMS campaigns to segments; consent is checked for every message.',
    },
    {
      title: 'Lead sources',
      body: 'Settings → Fields, lists & rules keeps the list of sources your team picks from.',
    },
  ],
  crm_post_sales: [
    {
      title: 'Bookings',
      body: 'Bookings lists every customer file: applicants, documents, demand letters and receipts.',
    },
    { title: 'Collections', body: 'Receivables shows what is due and overdue, project by project.' },
    { title: 'Construction updates', body: 'Send progress updates to every buyer of a project in one go.' },
  ],
  finance: [
    {
      title: 'Cash position',
      body: 'Your dashboard shows collections, overdue receivables, refunds and partner payouts.',
    },
    { title: 'Receivables', body: 'Receivables breaks money due down by age, project and booking.' },
    { title: 'Export', body: 'Every dashboard downloads as Excel or PDF.' },
  ],
};
TOURS['org_super_admin'] = [
  {
    title: 'Set up once',
    body: 'The set-up wizard walks you through company details, projects, people and lead sources.',
  },
  ...TOURS['director']!.slice(0, 2),
  TOURS['director']![2]!,
];

export function tourFor(roleKey: string): Step[] {
  return TOURS[roleKey] ?? SELLING;
}

export function FirstRunTour() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [closed, setClosed] = useState(false);
  if (!me || closed) return null;
  const key = `home:${me.role.key}`;
  const seen = me.member.toursSeen ?? [];
  if (seen.includes(key) || seen.includes('all')) return null;
  const steps = tourFor(me.role.key);
  const s = steps[step]!;
  const last = step === steps.length - 1;
  const finish = () => {
    setClosed(true);
    void post('/auth/me/tours', { tour: key }).then(() => qc.invalidateQueries({ queryKey: ['me'] }));
  };
  return (
    <aside
      role="complementary"
      aria-label="Getting started"
      data-testid="first-run-tour"
      className="fixed right-4 bottom-24 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-border bg-surface p-4 shadow-xl lg:bottom-6"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
          <Compass className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-text-muted">
            Getting started · {step + 1} of {steps.length}
          </p>
          <h2 className="font-display font-semibold text-text">{s.title}</h2>
          <p className="mt-1 text-sm text-text-muted">{s.body}</p>
        </div>
        <button
          type="button"
          aria-label="Skip the tour"
          className="text-text-faint hover:text-text"
          onClick={finish}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex gap-1" aria-hidden="true">
          {steps.map((_, i) => (
            <span key={i} className={cn('h-1.5 w-4 rounded-full', i === step ? 'bg-primary' : 'bg-border')} />
          ))}
        </div>
        <div className="flex gap-2">
          {step > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          <Button size="sm" onClick={() => (last ? finish() : setStep(step + 1))}>
            {last ? 'Done' : 'Next'}
          </Button>
        </div>
      </div>
    </aside>
  );
}
